import React, { useEffect, useState } from 'react';
import {
  Button,
  DialogPlugin,
  Dropdown,
  Form,
  Input,
  MessagePlugin,
  Space,
  Table,
  Tag,
  Tooltip,
} from 'tdesign-react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { useMemoizedFn } from 'ahooks';
import { useNavigate } from 'react-router-dom';
import { HelpCircleIcon } from 'tdesign-icons-react';
import { connect } from 'react-redux';
import { produce } from 'immer';
import {
  clearBacklog,
  getSubscriptionInternalStats,
  getSubscriptionStats,
  getSubscriptions,
} from '@/services/utilService';
import { deal70101, isVoid, renderByteRate, renderNum, renderNumRate } from '@/utils/common';
import SvgIcon from '@/components/SvgIcon';
import useTable from '@/hooks/useTable';
import Status from '@/pages/Cluster/Utils/Message/Subscription/Status';
import Online from '@/pages/Cluster/Utils/Message/Subscription/Online';
import Dashboard from '@/pages/Cluster/Utils/Message/Subscription/Dashboard';
import PopupLink from '@/components/Cluster/PopupLink';
import Detail from '@/pages/Cluster/Utils/Message/Subscription/Detail';
import { deleteSubscription, setReplicatedStatus } from '@/services/topicService';
import Offset from '@/pages/Cluster/Utils/Message/Subscription/Detail/Offset';
import ColTip from '@/components/Tip';
import PermWrapper from '@/components/PermWrapper';
import JSONModal from '@/components/JSONModal';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';
import Modal from './Modal';
import ModalKeyMatch from './ModalKeyMatch';

const { FormItem } = Form;

function Subscription({ commonProps, namespace, perms }) {
  const { clusterName } = commonProps;
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [visibleForm, setVisibleForm] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const [offsetVisible, setOffsetVisible] = useState(false);
  const [previewParams, setPreviewParams] = useState({});
  const [keyMatchVisible, setKeyMatchVisible] = useState(false);
  const confirmButton = useDisableButton({ content: '确定' });

  const options = [
    {
      content: '重置偏移量',
      resource: 'pulsar:topic:list:subOffset',
      value: 1,
    },
    {
      content: '清除积压',
      resource: 'pulsar:topic:list:subClearBacklog',
      value: 2,
    },
    {
      content: '订阅详情',
      resource: 'pulsar:topic:list:subDetail',
      value: 3,
    },
    {
      content: '偏移量详情',
      resource: 'pulsar:topic:list:subOffsetDetail',
      value: 4,
    },
    {
      content: '开关订阅复制',
      resource: 'pulsar:topic:list:replicated',
      value: 7,
    },
    {
      content: <span style={{ color: 'var(--td-error-color' }}>删除</span>,
      resource: 'pulsar:topic:list:subDelete',
      value: 5,
    },
  ].filter((item) => perms.includes(item.resource) || isVoid(item.resource));

  const columns = [
    {
      colKey: 'subscription',
      title: '订阅名',
      fixed: 'left',
      width: 280,
      sorter: true,
      cell: ({ row }) => (
        <PopupLink
          tip='跳转工具-订阅查询'
          onClick={() => {
            navigate(
              `/cluster/utils/message?name=${clusterName}&subscription=${row.subscription}&topic=${row.topic}&namespace=${namespace}`,
            );
          }}
        >
          <Detail resource={'pulsar:topic:list'} detail={row} />
        </PopupLink>
      ),
    },
    // {
    //   colKey: 'topic',
    //   title: 'Topic',
    //   width: 350,
    // },

    {
      colKey: 'msgBacklog',
      title: '积压条数',
      cell: renderNum,
      sorter: true,
      width: 120,
    },

    {
      colKey: 'rateOut',
      title: '消费速率',
      sortType: 'all',
      cell: renderNumRate,
      sorter: true,
      width: 120,
    },
    {
      colKey: 'bytesOut',
      title: '消费吞吐',
      cell: renderByteRate,
      sortType: 'all',
      sorter: true,
      width: 120,
    },

    {
      colKey: 'msgDelayed',
      title: (
        <ColTip
          content={
            <>
              <strong>延迟消息</strong>
              <li>还未达到消费时间的消息条数。</li>
            </>
          }
        >
          延迟消息
        </ColTip>
      ),
      sorter: true,
      cell: renderNum,
      width: 140,
    },
    {
      colKey: 'subscriptionType',
      title: (
        <ColTip
          content={
            <>
              <strong>订阅类型</strong>
              <li>当订阅者存在实际消费者时才会有值，除了Key_Shared模式。</li>
            </>
          }
        >
          订阅类型
        </ColTip>
      ),
      width: 140,
      sorter: true,
    },
    {
      colKey: 'replicated',
      title: '订阅复制',
      cell: ({ row }) => (
        <Status active={row.replicated}>{row.replicated ? '开启' : '关闭'}</Status>
      ),
      width: 100,
    },
    {
      colKey: 'subscriptionState',
      title: '状态',
      sorter: true,
      cell: ({ row }) => (
        <Status active={row.subscriptionState}>{row.subscriptionState ? '在线' : '离线'}</Status>
      ),
      width: 100,
    },
    {
      colKey: 'consumerOnlineCount',
      title: '在线个数',
      sorter: true,
      cell: ({ row }) => <Online clusterName={clusterName} detail={row} />,
      width: 120,
    },
    {
      colKey: 'app',
      title: '应用',
      width: 100,
    },
    {
      colKey: 'updateTime',
      title: '采集时间',
      cell: ({ row }) => dayjs(row.updateTime).format('YYYY-MM-DD HH:mm:ss'),
      sorter: true,
      width: 200,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space size='small'>
          <Icon
            type='presentation-chart-line'
            size={18}
            onClick={() => {
              setVisible(true);
              setCurrentRow(row);
            }}
          />
          <Dropdown
            options={produce(options, (draft) => {
              const hasKeyShared = (row.subscriptionType || '').toLowerCase() === 'key_shared';
              if (hasKeyShared) {
                draft.splice(4, 0, { content: '查看key归属', value: 6 });
              }
            })}
            onClick={({ value }) => clickHandler(value, row)}
          >
            <span>
              <Icon size={18} type='dots-vertical' />
            </span>
          </Dropdown>
        </Space>
      ),
      width: 80,
    },
  ];

  const onClearBacklogConfirm = async (dialog, row) => {
    try {
      dialog.update({
        confirmBtn: { loading: true, content: '确定' },
      });
      const { meta } = await clearBacklog({
        clusterName,
        topic: row.topic,
        subName: row.subscription,
      });
      // 主集群创建的资源已在灾备集群存在
      if (meta.code === 70101) {
        deal70101(meta.publicMessage, () => onClearBacklogConfirm(dialog, row), {
          url: '/cluster/topic/list',
          topic: row.topic,
        });
        return;
      }
      MessagePlugin.success('操作成功！');
      dialog.hide();
      doRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      dialog.update({
        confirmBtn: { loading: false, content: '确定' },
      });
    }
  };

  const onDeleteSubscriptionConfirm = async (dialog, row) => {
    try {
      dialog.update({
        confirmBtn: { loading: true, content: '确定' }, // 确定
      });
      const { meta } = await deleteSubscription({ ...commonProps, subscription: row.subscription });
      // 主集群创建的资源已在灾备集群存在
      if (meta.code === 70101) {
        deal70101(meta.publicMessage, () => onDeleteSubscriptionConfirm(dialog, row), {
          url: '/cluster/utils/message',
          subscription: row.subscription,
        });
        return;
      }
      MessagePlugin.success('操作成功！请稍后');
      dialog.hide();
      doRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      dialog.update({
        confirmBtn: { loading: false, content: '确定' }, // 确定
      });
    }
  };

  const onReplicateSubscriptionConfirm = async (dialog, row) => {
    try {
      dialog.update({
        confirmBtn: { loading: true, content: '确定' }, // 确定
      });
      const { meta } = await setReplicatedStatus({
        ...commonProps,
        subscription: row.subscription,
        enabled: !row.replicated,
      });

      // 主集群创建的资源已在灾备集群存在
      if (meta.code === 70101) {
        deal70101(meta.publicMessage, () => onReplicateSubscriptionConfirm(dialog, row), {
          url: '/cluster/utils/message',
          subscription: row.subscription,
        });
        return;
      }

      MessagePlugin.success('操作成功！');
      dialog.hide();
      doRefresh();
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      dialog.update({
        confirmBtn: { loading: false, content: '确定' }, // 确定
      });
    }
  };

  const clickHandler = async (value, row) => {
    setCurrentRow(row);
    if (value === 1) {
      // 重置偏移量
      setOffsetVisible(true);
    }
    if (value === 2) {
      const dialog = DialogPlugin.confirm({
        header: '确认清理积压？',
        theme: 'warning',
        body: (
          <Space breakLine size={[6, 6]}>
            清除Topic
            <Tag>{row.topic}</Tag>
            中订阅名为
            <Tag>{row.subscription}</Tag>
            所有积压
          </Space>
        ),
        confirmBtn: confirmButton,
        onClose: () => dialog.hide(),
        onConfirm: () => onClearBacklogConfirm(dialog, row),
      });
    }
    if (value === 3) {
      setPreviewParams({ visible: true, loading: true });
      // 订阅详情
      const { data } = await getSubscriptionStats({
        clusterName,
        topic: row.topic,
        subscription: row.subscription,
      });
      setPreviewParams({
        json: data,
        title: (
          <Header>
            订阅详情
            <HeaderTag>订阅名：{row.subscription}</HeaderTag>
            <HeaderTag>Topic：{row.topic}</HeaderTag>
            {row.isPartitioned && (
              <HeaderTag>
                分区：
                <Tooltip content='消费者连接信息需要在单分区中查看'>
                  <HelpCircleIcon size={14} style={{ marginTop: -2, cursor: 'pointer' }} />
                </Tooltip>
              </HeaderTag>
            )}
          </Header>
        ),
        visible: true,
        loading: false,
      });
    }

    if (value === 4) {
      setPreviewParams({ visible: true, loading: true });
      // 偏移量
      const { data } = await getSubscriptionInternalStats({
        clusterName,
        topic: row.topic,
        subscription: row.subscription,
      });
      setPreviewParams({
        json: data,
        title: <Header>偏移量详情</Header>,
        visible: true,
        loading: false,
      });
    }
    if (value === 5) {
      const dialog = DialogPlugin.confirm({
        header: '提示',
        theme: 'warning',
        body: (
          <Space size='small' breakLine>
            确定删除订阅<Tag>{row.subscription}</Tag>
          </Space>
        ),
        confirmBtn: confirmButton,
        onClose: () => dialog.hide(),
        onConfirm: () => onDeleteSubscriptionConfirm(dialog, row),
      });
    }

    if (value === 7) {
      const dialog = DialogPlugin.confirm({
        header: `确认${row.replicated ? '关闭' : '开启'}订阅复制？`,
        theme: 'warning',
        body: (
          <Space breakLine size={[6, 6]}>
            确认
            <Tag>{row.replicated ? '关闭' : '开启'}</Tag>
            Topic
            <Tag>{row.topic}</Tag>
            的订阅复制
          </Space>
        ),
        confirmBtn: {
          content: '确定',
        },
        onClose: () => dialog.hide(),
        onConfirm: () => onReplicateSubscriptionConfirm(dialog, row),
      });
    }

    if (value === 6) {
      setKeyMatchVisible(true);
    }
  };

  const loadData = useMemoizedFn(async (params = {}) => {
    const { data } = await getSubscriptions({ ...params, ...commonProps });
    setData(data);
  });

  const { header, config, setData, doSearch, doRefresh } = useTable({
    columns,
    search: (
      <FormItem name='subscription'>
        <Input placeholder='请输入订阅名' clearable />
      </FormItem>
    ),
    onSearch: loadData,
    operators: (
      <PermWrapper resource='pulsar:topic:list:subCreate'>
        <Button
          theme='primary'
          onClick={() => {
            setVisibleForm(true);
          }}
        >
          创建订阅
        </Button>
      </PermWrapper>
    ),
  });

  useEffect(() => {
    if (clusterName) {
      doSearch();
    }
  }, [doSearch, clusterName]);

  const handleClose = () => {
    setVisible(false);
  };

  const modalClose = (refresh) => {
    setVisibleForm(false);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <Dashboard
        clusterName={clusterName}
        visible={visible}
        detail={currentRow}
        handleClose={handleClose}
      />
      <Modal visible={visibleForm} handleClose={modalClose} commonProps={commonProps} />
      <Offset
        onRefresh={doRefresh}
        visible={offsetVisible}
        detail={{ ...currentRow, clusterName }}
        handleClose={() => setOffsetVisible(false)}
      />
      <JSONModal {...previewParams} handleClose={() => setPreviewParams({})} />
      <ModalKeyMatch
        visible={keyMatchVisible}
        handleClose={() => setKeyMatchVisible(false)}
        params={{
          ...commonProps,
          isPartition: currentRow.isPartitioned,
          subscription: currentRow.subscription,
        }}
      />
    </Root>
  );
}

export default connect((state) => ({
  perms: state.auth.perms,
}))(Subscription);

const Root = styled.div`
  margin-top: 20px;
`;

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;

const Header = styled.div`
  display: flex;
`;

const HeaderTag = styled.div`
  background: rgba(102, 136, 255, 0.12);
  border-radius: 6px;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  padding: 2px 10px;
  color: #6688ff;
  margin-left: 12px;
`;
